/**
 * BarChartV2 - Recharts-based bar chart component
 * 
 * Used in: Biospecimen Profile modal (expanded view)
 * Library: Recharts
 * 
 * Key features:
 * - Interactive legend on the right side (highlights on hover)
 * - Custom tooltip without arrow (matches production behavior)
 * - Alternating row colors in legend
 * - Synchronized hover state between bars and legend items
 */
import React, { useState, useEffect } from 'react';
import { withStyles } from '@material-ui/core';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
} from 'recharts';

const styles = theme => ({
  legendWrapper: {
    paddingLeft: '20px',
  },
  legend: {
    // marginBottom: '10px',
    padding: '0px 8px',
    display: 'flex',
    gap: '8px',
  },
  legendIcon: {},
  groupText: {
    fontFamily: 'Inter',
    fontWeight: '400',
    fontSize: '14px',
    color: '#444444',
  },
  countText: {
    fontFamily: 'Inter',
    fontWeight: '700',
    fontSize: '14px',
    color: '#444444',
  },
  tooltipWrapper: {
    backgroundColor: '#fff',
    padding: '0px 10px',
    border: '1px solid #929292',
    boxShadow: '0px 4px 4px 0px #00000040',
    display: 'flex',
    gap: '4px',
    position: 'relative',
  },
  tooltipArrow: {
    position: 'absolute',
    left: '50%',
    bottom: '-6.8px',
    width: '12px',
    height: '12px',
    backgroundColor: '#fff',
    borderTop: 'none',
    borderRight: '1px solid #929292',
    borderBottom: '1px solid #929292',
    borderLeft: 'none',
    transform: 'translateX(-50%) rotate(45deg)',
  },
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  groupTooltipText: {
    fontFamily: 'Inter',
    fontWeight: 400,
    fontSize: '13px',
    color: '#444444',
  },
  countTooltipText: {
    fontFamily: 'Inter',
    fontWeight: 700,
    fontSize: '13px',
    color: '#444444',
  },
});

const BarChartV2 = ({
  chartData,
  palette,
  xAxisLabel,
  yAxisLabel,
  tooltipCursor,
  classes,
}) => {
  const [hoveredGroup, setHoveredGroup] = useState(null);


  const CustomTooltip = ({ active, payload }) => {
    const currentGroup = active ? (payload?.[0]?.payload?.group || null) : null;

    useEffect(() => {
      setHoveredGroup(currentGroup);
    }, [currentGroup]);

    if (active && payload && payload.length) {
      const data = payload[0].payload;

      return (
        <div className={classes.tooltipWrapper}>
          <p className={classes.groupTooltipText}>{`${data.group},`}</p>
          <p className={classes.countTooltipText}>{`${data.count}`}</p>
        </div>
      );
    }

    return null;
  };

  const CustomLegend = ({ data, colors }) => (
    <div className={classes.legendWrapper}>
      {data.map((entry, index) => (
        <div
          key={`item-${index}`}
          className={classes.legend}
          style={{
            backgroundColor: index % 2 === 0 ? '#f2f2f2' : 'transparent',
            fontWeight: hoveredGroup === entry.group ? 'bold' : 'normal',
            boxShadow:
              hoveredGroup === entry.group
                ? '0px 0px 10px rgba(0,0,0,0.5)'
                : 'none',
            marginBottom: hoveredGroup === entry.group && index % 2 !== 0 ? '3px' : '0px',
          }}
        >
          <span
            className={classes.legendIcon}
            style={{ color: colors[index % colors.length] }}
          >
            ■
          </span>
          <div className={classes.groupText}>{entry.group}</div>{' '}
          <div className={classes.countText}>{entry.count}</div>
        </div>
      ))}
    </div>
  );

  return (
    <div className={classes.container}>
      <BarChart
        width={700}
        height={400}
        data={chartData}
        margin={{
          top: 5,
          right: 30,
          left: 20,
          bottom: 5,
        }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="group"
          tick={false}
          label={{
            value: xAxisLabel,
            dy: 10,
            style: { fontFamily: 'Inter', fontWeight: '400', color: '#444444' },
          }}
        />
        <YAxis
          domain={[0,dataMax => (Math.ceil(dataMax/100)*100)]}
          label={{
            value: yAxisLabel,
            angle: -90,
            position: 'insideLeft',
            dy: 50,
            style: { fontFamily: 'Inter', fontWeight: '400', color: '#444444' },
          }}
        />
        <Tooltip
          content={<CustomTooltip />}
          cursor={tooltipCursor}
          isAnimationActive={false}
          allowEscapeViewBox={{ x: false, y: false }}
          wrapperStyle={{
            transform: 'translateX(-50%)',
            pointerEvents: 'none',
          }}
          offset={-80}
        />
        <Bar dataKey="count">
          {chartData.map((_entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={palette[index % palette.length]}
            />
          ))}
        </Bar>
      </BarChart>
      <CustomLegend data={chartData} colors={palette} />
    </div>
  );
};

export default withStyles(styles)(BarChartV2);